/**
 * Typed API client for communicating with the AWS backend.
 * Adapted from extension/src/storage/api-client.ts:
 *   chrome.storage.session → sessionStorage
 *   chrome.storage.local   → localStorage
 */

import type {
  AcceptInviteRequest,
  ApproveResetRequest,
  AuthResponse,
  Circle,
  CreateAttachmentRequest,
  CreateAttachmentResponse,
  GetAttachmentResponse,
  CreateOrgRequest,
  CreateOrgResponse,
  DesignateRecoveryContactRequest,
  EncryptedVaultItem,
  GetAccountKeyResponse,
  GetIdentityKeyResponse,
  UploadIdentityKeyRequest,
  SignedKey,
  InviteeKeyResponse,
  InviteMemberRequest,
  InviteMemberResponse,
  ListCirclesResponse,
  ListCategoriesResponse,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  UpdateMemberNameRequest,
  CategorySummary,
  ListInboundRecoveryResponse,
  ListMembersResponse,
  ListOrgsResponse,
  ListPendingInvitesResponse,
  ListOrgInvitesResponse,
  FinalizeEmailInviteRequest,
  FinalizeEmailInviteResponse,
  ListRecoveryContactsResponse,
  ListUsageEventsResponse,
  OpaqueLoginFinishRequest,
  OpaqueLoginFinishResponse,
  OpaqueLoginStartRequest,
  OpaqueLoginStartResponse,
  OpaqueRegisterFinishRequest,
  OpaqueRegisterStartRequest,
  OpaqueRegisterStartResponse,
  PasswordRotateStartRequest,
  PasswordRotateStartResponse,
  PasswordRotateFinishRequest,
  PasswordRotateFinishResponse,
  RecoveryAuditResponse,
  RecoveryFinishRequest,
  RecoveryFinishResponse,
  RecoveryLookupRequest,
  RecoveryLookupResponse,
  RecoveryRequestCodeRequest,
  RecoveryRequestCodeResponse,
  RecoveryDenyRequest,
  RecoveryDenyResponse,
  RecoveryOpaqueStartRequest,
  RecoveryOpaqueStartResponse,
  RecoveryRequestStartRequest,
  RecoveryRequestStartResponse,
  RecoveryResetPackageResponse,
  RecoveryStatusResponse,
  RecoveryWrappedVaultKeyResponse,
  RefreshRequest,
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
  UploadAccountKeyRequest,
  VerifyEmailRequest,
  VerifyEmailResponse,
  ApiResponse,
  SupportChatRequest,
  SupportChatResponse,
} from '@heybesafe/shared';
import { resolveApiBaseUrl } from './apiBaseUrl';
import { isEntitlementErrorResponse, API_VERSION, HDR_API_VERSION, HDR_CLIENT } from '@heybesafe/shared';
import type { EntitlementErrorBody, EntitlementErrorCode } from '@heybesafe/shared';

// Re-export ApiResponse for convenience in wrapper modules like entitlement.ts
export type { ApiResponse };

// ─── Entitlement-error callback ─────────────────────────────────────────────
// Set by SafeContext on mount. doFetch calls this whenever a 4xx response
// body matches the EntitlementErrorBody contract — the handler is responsible
// for refreshing the snapshot and surfacing a transient banner. Keeping the
// callback out-of-band (vs. throwing) preserves the existing ApiResponse<T>
// discriminated-union pattern used by every web/ page.
type EntitlementErrorHandler = (body: EntitlementErrorBody) => void;
let entitlementErrorHandler: EntitlementErrorHandler | null = null;

// SP8: Skip list for non-snapshot entitlement codes. These errors don't reflect
// subscription state changes, so refreshing the snapshot + showing a banner is misleading.
const NON_SNAPSHOT_ENTITLEMENT_CODES = new Set<EntitlementErrorCode>([
  'EMAIL_NOT_VERIFIED',
  'SURFACE_SWITCH_RATE_LIMIT',
  'NOT_YOUR_SUBSCRIPTION',
]);

export function setEntitlementErrorHandler(
  handler: EntitlementErrorHandler | null,
): void {
  entitlementErrorHandler = handler;
}

// Runtime-derived per page load — see ./apiBaseUrl.ts for the host → API map.
// VITE_API_BASE_URL remains a localhost-dev escape hatch only; production
// hostnames pick their API target by inspecting window.location.hostname.
const API_BASE_URL = resolveApiBaseUrl(
  window.location.hostname,
  (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL,
);

// ─── Token Management ─────────────────────────────────────────────────────────

export async function getAccessTokenForSignOut(): Promise<string | null> {
  return sessionStorage.getItem('accessToken');
}

// Shared promise for in-flight /auth/refresh — N parallel callers would
// otherwise each fire their own refresh and storm the network tab with 401s
// when the refresh token is expired.
let inFlightRefresh: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;
  const refreshResult = await refreshAuth({ refreshToken });
  if (!refreshResult.ok) {
    // Refresh token rejected — wipe everything so subsequent getApiToken
    // calls bail before hitting /auth/refresh again, and signal the app
    // to repaint as logged-out instead of leaving the user on the unlock
    // screen waiting to discover their session is dead.
    await clearTokens();
    window.dispatchEvent(new CustomEvent('hbs:auth-expired'));
    return null;
  }
  await storeRefreshedTokens(refreshResult.data);
  return refreshResult.data.idToken ?? refreshResult.data.accessToken ?? null;
}

async function getApiToken(): Promise<string | null> {
  const token = sessionStorage.getItem('idToken') ?? sessionStorage.getItem('accessToken');
  if (token) return token;

  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh().finally(() => { inFlightRefresh = null; });
  }
  return inFlightRefresh;
}

export async function storeTokens(auth: AuthResponse): Promise<void> {
  sessionStorage.setItem('accessToken', auth.accessToken);
  sessionStorage.setItem('idToken', auth.idToken);
  sessionStorage.setItem('userId', auth.userId);
  localStorage.setItem('refreshToken', auth.refreshToken);
}

/**
 * Persist only the fields a Cognito `/auth/refresh` actually returns
 * (`accessToken`, `idToken`). The server responds with `Partial<AuthResponse>`
 * because `userId` and `refreshToken` are not rotated by REFRESH_TOKEN_AUTH —
 * blindly running [storeTokens] on the partial would silently clobber the
 * existing `sessionStorage.userId` with the literal string `"undefined"` (JS
 * coerces `setItem(k, undefined)` that way), breaking any feature that
 * compares the stored userId for identity.
 */
export async function storeRefreshedTokens(
  refreshed: Partial<AuthResponse>,
): Promise<void> {
  if (refreshed.accessToken) sessionStorage.setItem('accessToken', refreshed.accessToken);
  if (refreshed.idToken) sessionStorage.setItem('idToken', refreshed.idToken);
}

/**
 * The current user's Cognito `sub`, used for self-vs-other identity checks
 * (e.g. filtering self out of a "who can I add?" list). Prefers the cached
 * `sessionStorage.userId` set at login, but falls back to decoding the `sub`
 * claim from the ID token JWT — this self-heals legacy sessions where a prior
 * refresh wrote the literal string `"undefined"` before [storeRefreshedTokens]
 * existed.
 */
export function getCurrentUserId(): string | null {
  const cached = sessionStorage.getItem('userId');
  if (cached && cached !== 'undefined') return cached;
  return decodeSubFromIdToken(sessionStorage.getItem('idToken'));
}

function decodeSubFromIdToken(idToken: string | null): string | null {
  if (!idToken) return null;
  const parts = idToken.split('.');
  if (parts.length !== 3) return null;
  try {
    // JWT payload is base64url-encoded; atob() expects standard base64 with
    // padding. Re-add the URL-safe substitutions AND restore `=` padding so
    // strict atob implementations (jsdom, Node) don't choke.
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    const payload = JSON.parse(atob(b64));
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

export async function clearTokens(): Promise<void> {
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('idToken');
  sessionStorage.removeItem('userId');
  localStorage.removeItem('refreshToken');
}

// ─── Request Helper ───────────────────────────────────────────────────────────

async function doFetch<T>(
  path: string,
  options: RequestInit,
  headers: Record<string, string>
): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  // 204 No Content has no body — and API Gateway's authorizer rejections
  // come back as `{ message: 'Unauthorized' }` (not our `{ error, code }` shape).
  // Parse defensively and fall back to status text so the UI never shows
  // a literal "Unknown error".
  const text = await res.text();
  const body: { error?: string; code?: string; message?: string } =
    text ? (() => { try { return JSON.parse(text) as Record<string, string>; } catch { return {}; } })() : {};
  if (!res.ok) {
    if (isEntitlementErrorResponse(body)
        && !NON_SNAPSHOT_ENTITLEMENT_CODES.has(body.code)
        && entitlementErrorHandler) {
      // Fire-and-forget — handler refreshes snapshot + surfaces a banner.
      // We still return ApiResponse so callers see ok:false with the code.
      try { entitlementErrorHandler(body); } catch { /* never let the handler break the request */ }
    }
    const errBody = body as { error?: string; code?: string; message?: string };
    const message = errBody.error ?? errBody.message ?? `HTTP ${res.status} ${res.statusText || ''}`.trim();
    return { ok: false, error: message, code: errBody.code ?? `HTTP_${res.status}` };
  }
  return { ok: true, data: (body as unknown) as T };
}

export async function request<T>(
  path: string,
  options: RequestInit = {},
  authenticated = true
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Client-Platform': 'web',
    [HDR_API_VERSION]: String(API_VERSION),
    [HDR_CLIENT]: `web/${__APP_VERSION__}`,
    ...(options.headers as Record<string, string>),
  };

  if (!authenticated) {
    try {
      return await doFetch<T>(path, options, headers);
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'Network error', code: 'NETWORK_ERROR' };
    }
  }

  try {
    const token = await getApiToken();
    if (!token) return { ok: false, error: 'Not authenticated', code: 'NOT_AUTHENTICATED' };
    headers['Authorization'] = `Bearer ${token}`;

    const result = await doFetch<T>(path, options, headers);

    // Token expired mid-session — clear it, refresh, and retry once
    if (!result.ok && result.code === 'HTTP_401') {
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('idToken');
      const newToken = await getApiToken();
      if (!newToken) return { ok: false, error: 'Not authenticated', code: 'NOT_AUTHENTICATED' };
      headers['Authorization'] = `Bearer ${newToken}`;
      return await doFetch<T>(path, options, headers);
    }

    return result;
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Network error', code: 'NETWORK_ERROR' };
  }
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const opaqueRegisterStart = (p: OpaqueRegisterStartRequest) =>
  request<OpaqueRegisterStartResponse>('/auth/opaque/register/start', { method: 'POST', body: JSON.stringify(p) }, false);

export const opaqueRegisterFinish = (p: OpaqueRegisterFinishRequest) =>
  request<AuthResponse>('/auth/opaque/register/finish', { method: 'POST', body: JSON.stringify(p) }, false);

export const opaqueLoginStart = (p: OpaqueLoginStartRequest) =>
  request<OpaqueLoginStartResponse>('/auth/opaque/login/start', { method: 'POST', body: JSON.stringify(p) }, false);

export const opaqueLoginFinish = (p: OpaqueLoginFinishRequest) =>
  request<OpaqueLoginFinishResponse>('/auth/opaque/login/finish', { method: 'POST', body: JSON.stringify(p) }, false);

// The Lambdas the login/register flow hits. A cold container adds ~1s per
// call, and the flow fans out across all five, so a fresh account creation
// after an idle period cold-starts them serially.
const WARMUP_PATHS = ['/warm/auth', '/warm/orgs', '/warm/users', '/warm/vault', '/warm/subscriptions'] as const;

/**
 * Fire async, unauthenticated warm-up pings at the auth + hydration Lambdas so
 * they're warm by the time the user submits the login/create-account form.
 * Best-effort and fully fire-and-forget: every failure is swallowed (a warm
 * ping must never surface an error or block the page). Safe to call on mount.
 */
export function warmupBackend(): void {
  for (const path of WARMUP_PATHS) {
    void request<{ warmed: boolean }>(path, { method: 'GET' }, false).catch(() => {});
  }
}

// ─── Account deletion (OPAQUE re-auth, JWT-gated) ─────────────────────────────
// The two-step delete handshake re-proves the caller's master password against
// their own OPAQUE record before tearing down the account. Both endpoints
// require a valid JWT — the server reads the caller's email from the verified
// claim, never from the request body, so a stolen sessionId can't be used to
// nuke a different account.

export interface DeleteAccountStartResponse {
  loginResponse: string;
  sessionId: string;
}

export interface SoleAdminBlocker {
  orgId: string;
  orgName: string;
  memberCount: number;
}

/** Discriminated-union response for the finalize endpoint. Unlike the generic
 *  `ApiResponse<T>`, the error branch preserves the optional `orgs` field that
 *  the 409 SOLE_ADMIN_BLOCK refusal carries — the standard `request<T>` helper
 *  collapses error bodies down to `{ error, code }` and would drop it. */
export type DeleteAccountFinalizeResponse =
  | { ok: true; data: { ok: true } }
  | { ok: false; error: string; code: string; orgs?: SoleAdminBlocker[] };

export const deleteAccountStart = (p: { startLoginRequest: string }) =>
  request<DeleteAccountStartResponse>('/auth/delete-account/start', { method: 'POST', body: JSON.stringify(p) });

/**
 * One-off `request`-flavored wrapper for /auth/delete-account/finalize. We do
 * not route through the generic `request<T>` because the 409 SOLE_ADMIN_BLOCK
 * response carries an `orgs` array that the generic `doFetch` collapses away
 * (its error shape is `{ error, code }`). The UI needs the full org list to
 * show the user which families they must hand off before they can delete, so
 * we replicate the auth/refresh/retry-once flow inline and preserve the body.
 */
export async function deleteAccountFinalize(
  // disbandOrgIds: families from a prior SOLE_ADMIN_BLOCK the user confirmed
  // should be deleted along with the account.
  p: { sessionId: string; finishLoginRequest: string; disbandOrgIds?: string[] },
): Promise<DeleteAccountFinalizeResponse> {
  const path = '/auth/delete-account/finalize';
  const body = JSON.stringify(p);

  const buildHeaders = (token: string): Record<string, string> => ({
    'Content-Type': 'application/json',
    'X-Client-Platform': 'web',
    [HDR_API_VERSION]: String(API_VERSION),
    [HDR_CLIENT]: `web/${__APP_VERSION__}`,
    'Authorization': `Bearer ${token}`,
  });

  const parseResponse = async (res: Response): Promise<DeleteAccountFinalizeResponse> => {
    const text = await res.text();
    let parsed: Record<string, unknown> = {};
    if (text) {
      try { parsed = JSON.parse(text) as Record<string, unknown>; } catch { parsed = {}; }
    }
    if (!res.ok) {
      const errMsg = typeof parsed.error === 'string'
        ? parsed.error
        : typeof parsed.message === 'string'
          ? parsed.message
          : `HTTP ${res.status} ${res.statusText || ''}`.trim();
      const errCode = typeof parsed.code === 'string' ? parsed.code : `HTTP_${res.status}`;
      const orgs = Array.isArray(parsed.orgs) ? (parsed.orgs as SoleAdminBlocker[]) : undefined;
      return orgs !== undefined
        ? { ok: false, error: errMsg, code: errCode, orgs }
        : { ok: false, error: errMsg, code: errCode };
    }
    return { ok: true, data: (parsed as unknown) as { ok: true } };
  };

  try {
    let token = await getApiToken();
    if (!token) return { ok: false, error: 'Not authenticated', code: 'NOT_AUTHENTICATED' };

    let res = await fetch(`${API_BASE_URL}${path}`, { method: 'POST', headers: buildHeaders(token), body });

    // Mirror request<T>'s one-shot 401 retry so a mid-handshake JWT expiry
    // doesn't surface as a spurious INVALID_SESSION. The finalize endpoint
    // returns 401 for three different reasons — only ONE of them (JWT expiry,
    // which API Gateway's authorizer surfaces with no `code` field) warrants
    // a refresh + retry. OPAQUE INVALID_CREDENTIALS / INVALID_SESSION /
    // SESSION_EXPIRED are application-level outcomes; a refresh would burn
    // through the rate limit and still hit the same OPAQUE failure on retry.
    if (res.status === 401) {
      const peekText = await res.clone().text();
      let peekCode: string | undefined;
      if (peekText) {
        try { peekCode = (JSON.parse(peekText) as { code?: string }).code; } catch { /* unparseable body → assume authorizer reject */ }
      }
      const isOpaqueLevel401 = peekCode === 'INVALID_CREDENTIALS'
        || peekCode === 'INVALID_SESSION'
        || peekCode === 'SESSION_EXPIRED';
      if (!isOpaqueLevel401) {
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('idToken');
        const newToken = await getApiToken();
        if (!newToken) return { ok: false, error: 'Not authenticated', code: 'NOT_AUTHENTICATED' };
        token = newToken;
        res = await fetch(`${API_BASE_URL}${path}`, { method: 'POST', headers: buildHeaders(token), body });
      }
    }

    return await parseResponse(res);
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Network error', code: 'NETWORK_ERROR' };
  }
}

export const refreshAuth = (p: RefreshRequest) =>
  request<AuthResponse>('/auth/refresh', { method: 'POST', body: JSON.stringify(p) }, false);

export const passwordRotateStart = (p: PasswordRotateStartRequest) =>
  request<PasswordRotateStartResponse>('/auth/password-rotate/start', { method: 'POST', body: JSON.stringify(p) });

export const passwordRotateFinish = (p: PasswordRotateFinishRequest) =>
  request<PasswordRotateFinishResponse>('/auth/password-rotate/finish', { method: 'POST', body: JSON.stringify(p) });

export const verifyEmail = (p: VerifyEmailRequest) =>
  request<VerifyEmailResponse>('/auth/verify-email', { method: 'POST', body: JSON.stringify(p) }, false);

export async function logout(): Promise<ApiResponse<void>> {
  const token = await getAccessTokenForSignOut();
  if (!token) return { ok: true, data: undefined };
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'X-Client-Platform': 'web',
    [HDR_API_VERSION]: String(API_VERSION),
    [HDR_CLIENT]: `web/${__APP_VERSION__}`,
  };
  // Per-device revoke: server reads body.refreshToken and runs Cognito
  // RevokeToken against *this* refresh token rather than nuking every
  // session for the user (GlobalSignOut). When we don't have one stored
  // (already cleared / never set), send an empty body — server treats
  // that as a no-op.
  const refreshToken = localStorage.getItem('refreshToken');
  const requestBody = refreshToken ? { refreshToken } : {};
  try {
    const res = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { ok: false, error: body.error ?? 'Logout failed', code: body.code ?? `HTTP_${res.status}` };
    }
    return { ok: true, data: undefined };
  } catch {
    return { ok: true, data: undefined };
  }
}

// ─── Vault Sync ───────────────────────────────────────────────────────────────

export const pullVault = (since?: string) =>
  request<SyncPullResponse>(`/vault/sync${since ? `?since=${encodeURIComponent(since)}` : ''}`);

export const pushVault = (p: SyncPushRequest) =>
  request<SyncPushResponse>('/vault/sync', { method: 'POST', body: JSON.stringify(p) });

// ─── Orgs ─────────────────────────────────────────────────────────────────────

export const createOrg    = (p: CreateOrgRequest)                    => request<CreateOrgResponse>('/orgs', { method: 'POST', body: JSON.stringify(p) });
// PKA: founder backfills/sets the signature over the family public key.
export const putOrgPublicKeySig = (orgId: string, publicKeySig: SignedKey) =>
  request<{ ok: true }>(`/orgs/${orgId}/public-key-sig`, { method: 'PUT', body: JSON.stringify({ publicKeySig }) });
export const listOrgs     = ()                                        => request<ListOrgsResponse>('/orgs');
export const getOrg       = (orgId: string)                          => request<{ org: unknown; members: ListMembersResponse['members'] }>(`/orgs/${orgId}`);
export const inviteMember = (orgId: string, p: InviteMemberRequest)  => request<InviteMemberResponse>(`/orgs/${orgId}/invites`, { method: 'POST', body: JSON.stringify(p) });
export const acceptInvite = (orgId: string, p: AcceptInviteRequest)  => request<unknown>(`/orgs/${orgId}/invites/${p.token}/accept`, { method: 'POST', body: JSON.stringify(p) });
export const getPendingInvites = ()                                  => request<ListPendingInvitesResponse>('/users/me/invites');
export const declineInvite = (orgId: string, token: string)          => request<{ ok: true }>(`/orgs/${orgId}/invites/${token}`, { method: 'DELETE' });
export const listOrgInvites = (orgId: string)                        => request<ListOrgInvitesResponse>(`/orgs/${orgId}/invites`);
export const revokeOrgInvite = (orgId: string, token: string)        => request<{ ok: true }>(`/orgs/${orgId}/invites/${token}`, { method: 'DELETE' });
export const finalizeEmailInvite = (orgId: string, token: string, p: FinalizeEmailInviteRequest) => request<FinalizeEmailInviteResponse>(`/orgs/${orgId}/email-invites/${token}/finalize`, { method: 'POST', body: JSON.stringify(p) });
export const removeMember = (orgId: string, userId: string)          => request<void>(`/orgs/${orgId}/members/${userId}`, { method: 'DELETE' });
export const deleteOrg    = (orgId: string)                          => request<void>(`/orgs/${orgId}`, { method: 'DELETE' });
export const leaveOrg     = (orgId: string)                          => request<{ message: string }>(`/orgs/${orgId}/leave`, { method: 'POST' });

export const getInviteeKey = (orgId: string, email: string) =>
  request<InviteeKeyResponse>(`/orgs/${orgId}/invitee-key?email=${encodeURIComponent(email)}`);

export const promoteMember = (orgId: string, userId: string) =>
  request<{ ok: true }>(`/orgs/${orgId}/members/${userId}/promote`, { method: 'POST' });

// ─── Circles ──────────────────────────────────────────────────────────────────

export const listCircles       = (orgId: string) => request<ListCirclesResponse>(`/orgs/${orgId}/circles`);
export const createCircle      = (orgId: string, name: string) => request<{ circle: Circle }>(`/orgs/${orgId}/circles`, { method: 'POST', body: JSON.stringify({ name }) });
export const renameCircle      = (orgId: string, circleId: string, name: string) => request<{ ok: true }>(`/orgs/${orgId}/circles/${circleId}`, { method: 'PATCH', body: JSON.stringify({ name }) });
export const deleteCircle      = (orgId: string, circleId: string) => request<{ ok: true }>(`/orgs/${orgId}/circles/${circleId}`, { method: 'DELETE' });
export const addCircleMember   = (orgId: string, circleId: string, userId: string) => request<{ ok: true }>(`/orgs/${orgId}/circles/${circleId}/members/${userId}`, { method: 'PUT' });
export const removeCircleMember = (orgId: string, circleId: string, userId: string) => request<{ ok: true }>(`/orgs/${orgId}/circles/${circleId}/members/${userId}`, { method: 'DELETE' });

// ─── Binder categories ──────────────────────────────────────────────────────
// Read-only here — the sidebar rail browses categories; management (create/
// rename/reorder/delete) lives in Family Settings. Names arrive ECIES-wrapped;
// the client decrypts them with the family key (see crypto/categoryName.ts).

export const listCategories = (orgId: string) => request<ListCategoriesResponse>(`/orgs/${orgId}/categories`);
export const createCategory = (orgId: string, body: CreateCategoryRequest) => request<{ category: CategorySummary }>(`/orgs/${orgId}/categories`, { method: 'POST', body: JSON.stringify(body) });
export const updateCategory = (orgId: string, categoryId: string, body: UpdateCategoryRequest) => request<{ ok: true }>(`/orgs/${orgId}/categories/${categoryId}`, { method: 'PATCH', body: JSON.stringify(body) });
export const deleteCategory = (orgId: string, categoryId: string) => request<{ ok: true }>(`/orgs/${orgId}/categories/${categoryId}`, { method: 'DELETE' });

// Member display names. Self-only on the server: a member may set or clear only
// their own. The name is ECIES-wrapped to the family key client-side
// (crypto/memberName.ts) — the server stores an opaque blob.
export const updateMemberName = (orgId: string, userId: string, body: UpdateMemberNameRequest) =>
  request<{ ok: true }>(`/orgs/${orgId}/members/${userId}/name`, { method: 'PATCH', body: JSON.stringify(body) });
export const clearMemberName = (orgId: string, userId: string) =>
  request<{ ok: true }>(`/orgs/${orgId}/members/${userId}/name`, { method: 'DELETE' });

// Typed wrapper around the /vault/sync push for a single org-tagged item. The
// underlying endpoint is unchanged — this is a clarity wrapper for callers that
// want to share an already-encrypted item with the family.
export const shareItemWithFamily = (item: EncryptedVaultItem & { orgId: string; circleId?: string | null }) =>
  request<SyncPushResponse>('/vault/sync', {
    method: 'POST',
    body: JSON.stringify({ items: [item], deletedIds: [] }),
  });

// ─── Binder attachments (presigned S3; the Lambda never sees bytes) ──
// Unlike most wrappers above, these unwrap ApiResponse<T> and throw on
// failure — attachments.ts's upload/download orchestration treats a failed
// presign as an exception to retry/report, not a discriminated union to check.
async function requestOrThrow<T>(path: string, options?: RequestInit): Promise<T> {
  const result = await request<T>(path, options);
  if (!result.ok) throw new Error(result.error);
  return result.data;
}

export const createAttachmentUpload = (req: CreateAttachmentRequest) =>
  requestOrThrow<CreateAttachmentResponse>('/attachments', { method: 'POST', body: JSON.stringify(req) });

export const getAttachmentDownload = (attachmentId: string, params: { orgId: string; itemId: string }) =>
  requestOrThrow<GetAttachmentResponse>(`/attachments/${attachmentId}?${new URLSearchParams(params).toString()}`);

export const deleteAttachment = (attachmentId: string, params: { orgId: string; itemId: string }) =>
  requestOrThrow<void>(`/attachments/${attachmentId}?${new URLSearchParams(params).toString()}`, { method: 'DELETE' });

// ─── Account Key (Phase 3) ────────────────────────────────────────────────────

export const putAccountKey = (p: UploadAccountKeyRequest) =>
  request<{ ok: true }>('/users/me/account-key', { method: 'PUT', body: JSON.stringify(p) });

export const getAccountKey = () =>
  request<GetAccountKeyResponse>('/users/me/account-key');

// ─── Identity Key (PKA) ───────────────────────────────────────────────────────

export const putIdentityKey = (p: UploadIdentityKeyRequest) =>
  request<{ ok: true }>('/users/me/identity-key', { method: 'PUT', body: JSON.stringify(p) });

export const getIdentityKey = () =>
  request<GetIdentityKeyResponse>('/users/me/identity-key');

// ─── Usage Events ─────────────────────────────────────────────────────────────

export const getAdminInbox = (opts?: { since?: string; limit?: number; nextToken?: string }) => {
  const params = new URLSearchParams();
  if (opts?.since)     params.set('since', opts.since);
  if (opts?.limit)     params.set('limit', String(opts.limit));
  if (opts?.nextToken) params.set('nextToken', opts.nextToken);
  const qs = params.toString();
  return request<ListUsageEventsResponse>(`/events/admin-inbox${qs ? `?${qs}` : ''}`);
};

export const getOrgEvents = (orgId: string, opts?: { since?: string; limit?: number; nextToken?: string }) => {
  const params = new URLSearchParams();
  if (opts?.since)     params.set('since', opts.since);
  if (opts?.limit)     params.set('limit', String(opts.limit));
  if (opts?.nextToken) params.set('nextToken', opts.nextToken);
  const qs = params.toString();
  return request<ListUsageEventsResponse>(`/orgs/${orgId}/events${qs ? `?${qs}` : ''}`);
};

// ─── Recovery contacts (protected-user perspective, JWT-authed) ───────────────

export function designateRecoveryContact(
  body: DesignateRecoveryContactRequest,
): Promise<ApiResponse<{ created: true }>> {
  return request('/users/me/recovery-contacts', {
    method: 'POST', body: JSON.stringify(body),
  });
}

/**
 * Ask the server to email the prospective contact the "compare security words"
 * instructions. Fired when the designator reaches the verify step, because the
 * designation — and so the contact's own copy of the words — does not exist
 * until after verification completes.
 *
 * Server-side rate limited to one per pair per hour; a suppressed call still
 * returns success, so re-selecting a contact in the picker is silent.
 */
export function notifyRecoveryVerification(
  contactUserId: string,
): Promise<ApiResponse<void>> {
  return request(`/users/me/recovery-contacts/${encodeURIComponent(contactUserId)}/notify-verification`, {
    method: 'POST',
  });
}

export function listRecoveryContacts(): Promise<ApiResponse<ListRecoveryContactsResponse>> {
  return request('/users/me/recovery-contacts', { method: 'GET' });
}

export function removeRecoveryContact(
  contactUserId: string,
): Promise<ApiResponse<void>> {
  return request(`/users/me/recovery-contacts/${encodeURIComponent(contactUserId)}`, {
    method: 'DELETE',
  });
}

export function cancelEmergencyAccess(
  contactUserId: string,
): Promise<ApiResponse<{ cancelled: true }>> {
  return request(
    `/users/me/recovery-contacts/${encodeURIComponent(contactUserId)}/emergency-cancel`,
    { method: 'POST' },
  );
}

/** Owner-initiated cancel of an in-flight/approved password-reset recovery —
 *  returns the contact to ACTIVE and clears all reset material. */
export function cancelRecoveryReset(
  contactUserId: string,
): Promise<ApiResponse<{ cancelled: true }>> {
  return request(
    `/users/me/recovery-contacts/${encodeURIComponent(contactUserId)}/cancel-reset`,
    { method: 'POST' },
  );
}

export function getRecoveryAudit(): Promise<ApiResponse<RecoveryAuditResponse>> {
  return request('/users/me/recovery-audit', { method: 'GET' });
}

// ─── Recovery inbound (contact perspective, JWT-authed) ──────────────────────

export function listInboundRecovery(): Promise<ApiResponse<ListInboundRecoveryResponse>> {
  return request('/recovery/inbound', { method: 'GET' });
}

export function acceptDesignation(
  protectedUserId: string,
): Promise<ApiResponse<{ accepted: true }>> {
  return request(
    `/recovery/inbound/${encodeURIComponent(protectedUserId)}/accept`,
    { method: 'POST' },
  );
}

export function declineDesignation(
  protectedUserId: string,
): Promise<ApiResponse<void>> {
  return request(
    `/recovery/inbound/${encodeURIComponent(protectedUserId)}/decline`,
    { method: 'POST' },
  );
}

export function approveReset(
  protectedUserId: string, body: ApproveResetRequest,
): Promise<ApiResponse<{ approved: true }>> {
  return request(
    `/recovery/inbound/${encodeURIComponent(protectedUserId)}/approve`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export function denyReset(
  protectedUserId: string,
): Promise<ApiResponse<{ denied: true }>> {
  return request(
    `/recovery/inbound/${encodeURIComponent(protectedUserId)}/deny`,
    { method: 'POST' },
  );
}

export function requestEmergencyAccess(
  protectedUserId: string,
): Promise<ApiResponse<{ initiated: true; releaseAt: string }>> {
  return request(
    `/recovery/inbound/${encodeURIComponent(protectedUserId)}/emergency-request`,
    { method: 'POST' },
  );
}

export function getWrappedVaultKey(
  protectedUserId: string,
): Promise<ApiResponse<RecoveryWrappedVaultKeyResponse>> {
  return request(
    `/recovery/inbound/${encodeURIComponent(protectedUserId)}/wrapped-vault-key`,
    { method: 'GET' },
  );
}

export function getRecoveryItems(
  protectedUserId: string, since?: string,
): Promise<ApiResponse<SyncPullResponse>> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  return request(
    `/recovery/inbound/${encodeURIComponent(protectedUserId)}/items${qs}`,
    { method: 'GET' },
  );
}

export function getResetPackage(
  protectedUserId: string,
): Promise<ApiResponse<RecoveryResetPackageResponse>> {
  return request(
    `/recovery/inbound/${encodeURIComponent(protectedUserId)}/reset-package`,
    { method: 'GET' },
  );
}

// ─── Forgot-password flow (public, no JWT) ────────────────────────────────────

export function recoveryRequestCode(
  body: RecoveryRequestCodeRequest,
): Promise<ApiResponse<RecoveryRequestCodeResponse>> {
  return request('/auth/recovery/request-code', { method: 'POST', body: JSON.stringify(body) }, false);
}

export function recoveryLookup(
  body: RecoveryLookupRequest,
): Promise<ApiResponse<RecoveryLookupResponse>> {
  return request('/auth/recovery/lookup', { method: 'POST', body: JSON.stringify(body) }, false);
}

/** Owner's one-click deny of an in-flight recovery request (signed token from email). */
export function recoveryDeny(
  body: RecoveryDenyRequest,
): Promise<ApiResponse<RecoveryDenyResponse>> {
  return request('/auth/recovery/deny', { method: 'POST', body: JSON.stringify(body) }, false);
}

export function recoveryRequest(
  body: RecoveryRequestStartRequest,
): Promise<ApiResponse<RecoveryRequestStartResponse>> {
  return request('/auth/recovery/request', { method: 'POST', body: JSON.stringify(body) }, false);
}

export function recoveryStatus(
  recoverySessionToken: string,
): Promise<ApiResponse<RecoveryStatusResponse>> {
  // `cache: 'no-store'` is critical here — the server does not (yet) emit
  // Cache-Control headers, so without this flag the browser can apply
  // heuristic caching to the GET and the 5-second poll loop ends up
  // returning the same stale "RESET_REQUESTED" body forever even after the
  // contact has approved.
  return request('/auth/recovery/status', {
    method: 'GET',
    cache: 'no-store',
    headers: { Authorization: `Bearer ${recoverySessionToken}` },
  }, false);
}

export function recoveryFinish(
  recoverySessionToken: string, body: RecoveryFinishRequest,
): Promise<ApiResponse<RecoveryFinishResponse>> {
  return request('/auth/recovery/finish', {
    method: 'POST',
    headers: { Authorization: `Bearer ${recoverySessionToken}` },
    body: JSON.stringify(body),
  }, false);
}

// Recovery-flow OPAQUE register-start: same shape as opaqueRegisterStart but
// gated by the recoverySessionToken instead of an open POST. Used by
// ForgotPasswordPage.finalize() — the regular /auth/opaque/register/start
// 409s with EMAIL_EXISTS for an already-registered user.
export function recoveryOpaqueStart(
  recoverySessionToken: string, body: RecoveryOpaqueStartRequest,
): Promise<ApiResponse<RecoveryOpaqueStartResponse>> {
  return request('/auth/recovery/opaque-start', {
    method: 'POST',
    headers: { Authorization: `Bearer ${recoverySessionToken}` },
    body: JSON.stringify(body),
  }, false);
}

// ─── Support assistant ──────────────────────────────────────────────────────

/** Ask the in-app help assistant a how-to question. Sends conversation text
 *  only — never any vault data. */
export const supportChat = (p: SupportChatRequest) =>
  request<SupportChatResponse>('/support/chat', { method: 'POST', body: JSON.stringify(p) });
